import { useI18n } from "@/context/I18nContext";
import { Zap, ShieldCheck, Flame, Droplets, Clock, CheckCircle2, Award } from "lucide-react";

/**
 * Technical Infographic: Decapsulated Artemia Biological & Operational Advantages.
 * Displays energy conservation kinetics, pathogen elimination, and direct-feed operational workflow.
 */
export function DecapNutritionalInfographic() {
  const { t } = useI18n();

  return (
    <div className="glass-card rounded-3xl border border-border/80 bg-background shadow-xl p-6 md:p-8 space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-6 border-b border-border/60 gap-4">
        <div>
          <span className="text-xs font-bold text-mint-ink uppercase tracking-widest flex items-center gap-1.5 mb-1">
            <Zap className="h-4 w-4 text-mint-ink" /> Biological & Energy Efficiency
          </span>
          <h3 className="font-display text-xl md:text-2xl font-bold text-navy">
            Why Decapsulated Artemia Outperforms Standard Hatching
          </h3>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <span className="inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1 rounded-full bg-mint/15 text-navy border border-mint/30">
            <Award className="h-3.5 w-3.5 text-mint-ink" /> 502 kcal / 100g Biomass
          </span>
        </div>
      </div>

      {/* 3 Core Infographic Pillars */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Pillar 1: Metabolic Energy Conservation */}
        <div className="glass-card rounded-2xl p-5 border border-amber-500/20 bg-amber-500/5 space-y-4 flex flex-col justify-between">
          <div className="space-y-3">
            <div className="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-600">
              <Flame className="h-5 w-5" />
            </div>
            <h4 className="font-display text-base font-bold text-navy">
              100% Embryonic Energy Retained
            </h4>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Standard Artemia nauplii expend{" "}
              <strong>25–30% of their endogenous lipid and glycogen reserves</strong> breaking
              through the rigid chorion during the 24-hour incubation cycle.
            </p>
          </div>

          <div className="pt-3 border-t border-amber-500/15 space-y-2">
            <div className="flex justify-between items-center text-xs">
              <span className="text-muted-foreground font-medium">Standard Hatched Instar I</span>
              <span className="font-bold text-navy">~380 kcal/100g</span>
            </div>
            <div className="w-full bg-surface-alt rounded-full h-2 overflow-hidden">
              <div className="bg-amber-600 h-2 rounded-full" style={{ width: "75%" }} />
            </div>

            <div className="flex justify-between items-center text-xs pt-1">
              <span className="text-emerald-800 font-bold">Decapsulated Embryo</span>
              <span className="font-bold text-emerald-600">502 kcal/100g (+32%)</span>
            </div>
            <div className="w-full bg-surface-alt rounded-full h-2 overflow-hidden">
              <div className="bg-emerald-500 h-2 rounded-full" style={{ width: "100%" }} />
            </div>
          </div>
        </div>

        {/* Pillar 2: Complete Biosecurity & Vibrio Elimination */}
        <div className="glass-card rounded-2xl p-5 border border-emerald-500/20 bg-emerald-500/5 space-y-4 flex flex-col justify-between">
          <div className="space-y-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-600">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <h4 className="font-display text-base font-bold text-navy">
              Zero Shell · Zero Pathogen Vector
            </h4>
            <p className="text-xs text-muted-foreground leading-relaxed">
              The outer chorion is the primary colonization site for marine bacteria including{" "}
              <em>Vibrio spp.</em> Hypochlorite decapsulation completely dissolves the shell,
              sterilizing the embryo surface and preventing larval gut impaction.
            </p>
          </div>

          <ul className="pt-3 border-t border-emerald-500/15 space-y-1.5 text-xs text-navy/90">
            <li className="flex items-center gap-1.5 font-medium">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
              <span>0% indigestible chitin shells</span>
            </li>
            <li className="flex items-center gap-1.5 font-medium">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
              <span>No intestinal blockage in delicate fry</span>
            </li>
            <li className="flex items-center gap-1.5 font-medium">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
              <span>Surface disinfection during decapsulation</span>
            </li>
          </ul>
        </div>

        {/* Pillar 3: Operational Streamlining */}
        <div className="glass-card rounded-2xl p-5 border border-blue-500/20 bg-blue-500/5 space-y-4 flex flex-col justify-between">
          <div className="space-y-3">
            <div className="h-10 w-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-primary">
              <Clock className="h-5 w-5" />
            </div>
            <h4 className="font-display text-base font-bold text-navy">
              Direct Feeding · Zero Incubation Cones
            </h4>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Eliminate 24-hour hatching cycles, continuous aeration pumps, light banks, and messy
              light-separation steps. Simply hydrate dried decapsulated cysts in fresh or salt water
              for 20–30 minutes and distribute directly.
            </p>
          </div>

          <div className="pt-3 border-t border-blue-500/15 bg-background/60 rounded-xl p-3 space-y-1 text-xs">
            <div className="flex items-center justify-between font-bold text-navy">
              <span>Hydration SOP</span>
              <span className="text-primary font-mono text-[11px]">20–30 min soak</span>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Soaking softens the cuticular membrane and allows buoyant suspension in the larval
              water column.
            </p>
          </div>
        </div>
      </div>

      {/* Visual Workflow Steps (From Canister to Tank) */}
      <div className="bg-surface-alt/70 rounded-2xl p-5 border border-border/70 space-y-4">
        <h4 className="text-xs font-bold uppercase tracking-wider text-navy flex items-center gap-2">
          <Droplets className="h-4 w-4 text-mint-ink" />
          Standard Hatchery Preparation Protocol
        </h4>

        <div className="grid sm:grid-cols-4 gap-4 text-xs">
          <div className="p-3.5 rounded-xl bg-background border border-border/60 space-y-1.5">
            <div className="text-[10px] font-bold text-primary uppercase">Step 1 · Measure</div>
            <div className="font-bold text-navy text-sm">Weigh Dry Cysts</div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Dose exact dry grams per tank based on larval density (no hatch-percentage
              uncertainty).
            </p>
          </div>

          <div className="p-3.5 rounded-xl bg-background border border-border/60 space-y-1.5">
            <div className="text-[10px] font-bold text-primary uppercase">Step 2 · Hydrate</div>
            <div className="font-bold text-navy text-sm">20–30 Min Soak</div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Submerge in clean fresh or brackish water (10–15 L water per kg of cysts) with gentle
              aeration.
            </p>
          </div>

          <div className="p-3.5 rounded-xl bg-background border border-border/60 space-y-1.5">
            <div className="text-[10px] font-bold text-primary uppercase">Step 3 · Strain</div>
            <div className="font-bold text-navy text-sm">Screen & Rinse</div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Collect on a 100–120 µm plankton sieve; briefly rinse with system water before
              broadcast.
            </p>
          </div>

          <div className="p-3.5 rounded-xl bg-background border border-border/60 space-y-1.5">
            <div className="text-[10px] font-bold text-primary uppercase">Step 4 · Feed</div>
            <div className="font-bold text-navy text-sm">Direct Distribution</div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Evenly broadcast across larval tanks. Neutral buoyancy keeps embryos suspended in the
              water column.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
